import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import SeoHead from "@/components/feature/SeoHead";
import ArticleCard from "@/components/feature/ArticleCard";
import { articles, categories, getCategory, type CategoryId } from "@/mocks/content";

const origin = "https://zuori-chigua.example";

export default function News() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [keyword, setKeyword] = useState("");

  const activeRaw = searchParams.get("c");
  const active: CategoryId | "all" = categories.some((c) => c.id === activeRaw)
    ? (activeRaw as CategoryId)
    : "all";

  const pick = (id: CategoryId | "all") => {
    if (id === "all") {
      searchParams.delete("c");
      setSearchParams(searchParams, { replace: true });
    } else {
      setSearchParams({ c: id }, { replace: true });
    }
  };

  const list = useMemo(() => {
    let out = active === "all" ? articles : articles.filter((a) => a.category === active);
    const kw = keyword.trim().toLowerCase();
    if (kw) {
      out = out.filter(
        (a) =>
          a.title.toLowerCase().includes(kw) ||
          a.summary.toLowerCase().includes(kw) ||
          a.kicker.toLowerCase().includes(kw) ||
          a.tags.some((t) => t.toLowerCase().includes(kw)),
      );
    }
    return out;
  }, [active, keyword]);

  const activeCat = active === "all" ? null : getCategory(active);
  const catDesc = activeCat
    ? activeCat.desc
    : "浏览昨日吃瓜全部复盘内容：时间线梳理、隐藏线索比对、关联人物关系与未解之谜归档。";

  const schema = [
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: origin + "/" },
        { "@type": "ListItem", position: 2, name: "瓜田速递", item: origin + "/news" },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: (activeCat ? activeCat.name : "瓜田速递") + "｜昨日吃瓜",
      url: origin + "/news" + (activeCat ? "?c=" + active : ""),
      inLanguage: "zh-CN",
    },
  ];

  return (
    <>
      <SeoHead
        title={(activeCat ? activeCat.name + "｜" : "") + "瓜田速递｜昨日吃瓜"}
        description={catDesc + " 以公开信息为准，只提问、不定性，理性吃瓜。"}
        keywords={activeCat ? activeCat.name + ",昨日吃瓜," + activeCat.name + "分析" : "昨日吃瓜,瓜田速递,娱乐复盘"}
        path={activeCat ? `/news?c=${active}` : "/news"}
        ogType="website"
        jsonLd={schema}
      />
      <Header />
      <main>
        <div className="border-b border-background-300/60 bg-background-100/70">
          <div className="mx-auto max-w-7xl px-4 py-8 md:px-6 md:py-10">
            <nav className="mb-4 flex flex-wrap items-center gap-2 text-xs text-foreground-500" aria-label="面包屑">
              <Link to="/" className="flex items-center gap-1 transition-colors hover:text-primary-600">
                <i className="ri-home-4-line" aria-hidden="true" /> 首页
              </Link>
              <i className="ri-arrow-right-s-line" aria-hidden="true" />
              <span className="text-foreground-700">瓜田速递</span>
              {activeCat && (
                <>
                  <i className="ri-arrow-right-s-line" aria-hidden="true" />
                  <span className="font-medium text-primary-600">{activeCat.name}</span>
                </>
              )}
            </nav>
            <h1 className="font-display text-3xl font-black text-foreground-950 md:text-4xl">
              {activeCat ? activeCat.name : "瓜田速递"}
            </h1>
            <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
              {catDesc}
            </p>

            <div className="mt-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => pick("all")}
                  className={
                    "whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors " +
                    (active === "all"
                      ? "bg-primary-500 text-background-50"
                      : "bg-background-50 text-foreground-700 hover:bg-background-200")
                  }
                >
                  全部
                </button>
                {categories.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => pick(c.id)}
                    className={
                      "whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors " +
                      (active === c.id
                        ? "bg-primary-500 text-background-50"
                        : "bg-background-50 text-foreground-700 hover:bg-background-200")
                    }
                  >
                    {c.name}
                  </button>
                ))}
              </div>
              <div className="relative w-full lg:w-80">
                <i className="ri-search-line pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-foreground-500" aria-hidden="true" />
                <input
                  type="text"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="搜索标题、线索或栏目"
                  className="w-full rounded-full border border-background-300 bg-background-50 py-2.5 pl-11 pr-4 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-500 focus:border-primary-500"
                />
              </div>
            </div>
          </div>
        </div>

        <section className="bg-background-50">
          <div className="mx-auto max-w-7xl px-4 py-10 md:px-6 md:py-14">
            <div className="mb-6 flex items-center justify-between">
              <h2 className="text-sm font-semibold text-foreground-600">
                共 <span className="font-display text-lg font-black text-primary-600">{list.length}</span> 篇复盘内容
              </h2>
              <span className="hidden items-center gap-1 text-xs text-foreground-500 sm:flex">
                <i className="ri-loader-4-line" aria-hidden="true" /> 持续整理中
              </span>
            </div>

            {list.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-background-300 bg-background-100/50 py-20 text-center">
                <i className="ri-inbox-line text-4xl text-background-300" aria-hidden="true" />
                <p className="mt-3 font-display text-lg font-bold text-foreground-700">暂无匹配内容</p>
                <p className="mt-1 text-sm text-foreground-500">换个栏目或关键词再试试。</p>
                <button
                  type="button"
                  onClick={() => {
                    setKeyword("");
                    pick("all");
                  }}
                  className="mt-5 whitespace-nowrap rounded-full bg-primary-500 px-5 py-2 text-sm font-medium text-background-50 hover:bg-primary-600"
                >
                  清空筛选
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {list.map((a) => (
                  <ArticleCard key={a.id} article={a} />
                ))}
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}